'use client'

import { ArrowLeft24, X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Image, Navigation, Radio, Text } from '@vinted/web-ui'
import { useContext, useEffect } from 'react'

import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  GenerateLabelModal,
  LabelType,
  transformLabelOptionsResponse,
  getLabelOptions,
  LabelOptionsAvailableLabelTypesModel,
  ShipmentEntryType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { List } from '@marketplace-web/common-components/list-ui'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'

import { clickEvent, viewEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

type Props = {
  isOpen: boolean
}

const LabelTypeSelectionModal = ({ isOpen }: Props) => {
  const translate = useTranslate('conversation.get_shipping_label.select_label_type.modal')
  const commonTranslate = useTranslate('common.a11y.actions')
  const { track } = useTracking()

  const { conversation } = useConversationContext()
  const {
    state: {
      labelTypeSelection: {
        isLabelTypeSubmitEnabled,
        selectLabelType,
        clearLabelTypeSelection,
        selectedLabelType,
      },
    },
  } = useContext(GetShippingLabelContext)

  const shipmentId = conversation?.transaction?.shipment?.id
  const transactionId = conversation?.transaction?.id

  const {
    state: {
      activeModal: { setActiveModal },
      dropOffTypeSelection: { clearDropOffTypeSelection },
    },
  } = useContext(GetShippingLabelContext)

  const {
    fetch: fetchLabelOptions,
    transformedData: labelOptions,
    isLoading: isLabelOptionsLoading,
  } = useFetch(getLabelOptions, transformLabelOptionsResponse)

  useEffect(() => {
    if (shipmentId && isOpen) fetchLabelOptions(shipmentId)
  }, [shipmentId, isOpen, fetchLabelOptions])

  const isReturnShipment = labelOptions?.shipmentEntryType === ShipmentEntryType.Return
  const isPackageTypeLabelTypeFlow =
    labelOptions?.availableLabelTypes &&
    labelOptions?.availableLabelTypes.length > 1 &&
    labelOptions?.dropOffTypes &&
    labelOptions?.dropOffTypes.length > 1

  useEffect(() => {
    if (!isOpen || !transactionId) return

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
    })

    track(
      viewEvent({
        screen: Screen.LabelTypeSelection,
        target: 'label_type_selection',
        targetDetails,
      }),
    )
  }, [track, transactionId, isOpen])

  function renderCarrier() {
    if (!labelOptions?.carrier) return null

    return (
      <>
        <Cell
          prefix={
            <Image
              src={labelOptions?.carrier.iconUrl}
              size={Image.Size.Large}
              styling={Image.Styling.Rounded}
              alt={labelOptions?.carrier.name}
            />
          }
          title={labelOptions?.carrier.name}
          body={!isReturnShipment && translate('carrier_body')}
        />
        <Divider />
      </>
    )
  }

  function getDigitalLabelType() {
    switch (selectedLabelType) {
      case LabelType.LabelTypePdf:
        return 'printable'
      case LabelType.LabelTypeQr:
        return 'digital'
      default:
        return null
    }
  }

  function trackSubmitClick() {
    if (!transactionId) return

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
      label_type: getDigitalLabelType(),
    })

    track(
      clickEvent({
        screen: Screen.LabelTypeSelection,
        target: ClickableElement.SelectLabelType,
        targetDetails,
      }),
    )
  }

  const selectDigitalLabelType = (selectedId: LabelType) => () => {
    selectLabelType(selectedId)
  }

  function handleSubmit() {
    trackSubmitClick()
    if (isPackageTypeLabelTypeFlow && !isReturnShipment) {
      setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)

      return
    }

    setActiveModal(GenerateLabelModal.GetShippingLabelModal)
  }

  function renderLabelOptionsDigitalLabelTypes(
    digitalLabelType: LabelOptionsAvailableLabelTypesModel,
  ) {
    const { type, iconUrl, title, subtitle } = digitalLabelType

    return (
      <Cell
        key={type}
        type={Cell.Type.Navigating}
        prefix={<Image src={iconUrl} styling={Image.Styling.Rounded} />}
        title={title}
        body={<Text as="span" html text={subtitle} />}
        onClick={selectDigitalLabelType(type)}
        suffix={
          <Radio
            name={`${title}-radio`}
            checked={type === selectedLabelType}
            value={type}
            testId={`label-type-type-selection-modal-${type}-radio`}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': `label-type-type-selection-modal-${type}-radio`,
            }}
          />
        }
      />
    )
  }

  function renderLabelTypeSelection() {
    return (
      <>
        <Cell body={<Text as="h1" type={Text.Type.Heading} text={translate('body')} />} />
        <List>{labelOptions?.availableLabelTypes?.map(renderLabelOptionsDigitalLabelTypes)}</List>
        <Divider />
      </>
    )
  }

  function renderSubmit() {
    return (
      <div className="u-margin-top-auto">
        <Divider />
        <Cell>
          <Button
            styling={Button.Styling.Filled}
            text={translate('action.continue')}
            disabled={!isLabelTypeSubmitEnabled}
            onClick={handleSubmit}
            testId="label-type-type-selection-modal-submit-button"
          />
        </Cell>
      </div>
    )
  }

  function handleClose() {
    if (isPackageTypeLabelTypeFlow) clearDropOffTypeSelection()
    clearLabelTypeSelection()
    setActiveModal(GenerateLabelModal.None)
  }

  function handlePreviousClick() {
    if (isReturnShipment) {
      return setActiveModal(GenerateLabelModal.ReturnTheOrder)
    }

    return setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
  }

  function renderNavigationBackArrow() {
    return (
      isReturnShipment && (
        <Button
          styling={Button.Styling.Flat}
          iconName={ArrowLeft24}
          inline
          onClick={handlePreviousClick}
          testId="label-type-type-selection-modal-back-button"
        />
      )
    )
  }

  function renderModalContent() {
    if (isLabelOptionsLoading) return <ContentLoader testId="label-type-selection-modal-loader" />

    return (
      <>
        {renderCarrier()}
        {renderLabelTypeSelection()}
        {renderSubmit()}
      </>
    )
  }

  return (
    <Dialog hasScrollableContent show={isOpen} testId="label-type-selection-modal">
      <div className="u-fill-width">
        <ScrollableArea>
          <Navigation
            body={translate('title')}
            left={renderNavigationBackArrow()}
            right={
              <Button
                styling={Button.Styling.Flat}
                onClick={handleClose}
                iconName={X24}
                testId="close-selection-modal"
                inline
                aria={{ 'aria-label': commonTranslate('dialog_close') }}
              />
            }
          />
          <Divider />
          <div className="label-type-selection-modal__body">{renderModalContent()}</div>
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default LabelTypeSelectionModal
